/**
 * Hooks para los catálogos del sistema — Fase 8
 *
 * Cubre categorías, provincias, localidades y parentescos. Cada catálogo
 * tiene un hook compacto: list + create + update + delete (o toggle activo
 * cuando hay flag de soft-delete).
 *
 * Patrón #014: directo a Supabase. RLS:
 *   - SELECT: authenticated (todos los catálogos son públicos)
 *   - INSERT/UPDATE/DELETE: solo admin_general (matriz M14)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

// ── Tipos ────────────────────────────────────────────────────────────────────

export interface Categoria {
  id: string;
  codigo: string | null;
  descripcion: string;
  activa: boolean;
  created_at: string;
}

export interface Provincia {
  id: string;
  nombre: string;
  id_pais: number;
  created_at: string;
}

export interface Localidad {
  id: string;
  nombre: string;
  provincia_id: string;
  created_at: string;
  provincia?: { nombre: string } | null;
}

export interface Parentesco {
  id: string;
  descripcion: string;
  created_at: string;
}

// ── Query keys ───────────────────────────────────────────────────────────────

const QK = {
  categorias: ['catalogos', 'categorias'] as const,
  provincias: ['catalogos', 'provincias'] as const,
  localidades: (provinciaId?: string) =>
    ['catalogos', 'localidades', provinciaId ?? 'all'] as const,
  parentescos: ['catalogos', 'parentescos'] as const,
};

// ── Categorías ───────────────────────────────────────────────────────────────

export function useCategorias() {
  return useQuery({
    queryKey: QK.categorias,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categorias')
        .select('*')
        .order('codigo', { nullsFirst: false })
        .order('descripcion');
      if (error) throw error;
      return (data ?? []) as Categoria[];
    },
    staleTime: 60_000,
  });
}

export function useCreateCategoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      codigo: string | null;
      descripcion: string;
      activa: boolean;
    }) => {
      const { data, error } = await supabase
        .from('categorias')
        .insert(payload)
        .select('*')
        .single();
      if (error) throw error;
      return data as Categoria;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.categorias }),
  });
}

export function useUpdateCategoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: Partial<{ codigo: string | null; descripcion: string; activa: boolean }>;
    }) => {
      const { data, error } = await supabase
        .from('categorias')
        .update(payload)
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as Categoria;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.categorias }),
  });
}

// ── Provincias ───────────────────────────────────────────────────────────────

export function useProvincias() {
  return useQuery({
    queryKey: QK.provincias,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('provincias')
        .select('*')
        .order('nombre');
      if (error) throw error;
      return (data ?? []) as Provincia[];
    },
    staleTime: 5 * 60_000, // las provincias casi no cambian
  });
}

export function useCreateProvincia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (nombre: string) => {
      const { data, error } = await supabase
        .from('provincias')
        .insert({ nombre })
        .select('*')
        .single();
      if (error) throw error;
      return data as Provincia;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.provincias }),
  });
}

export function useUpdateProvincia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, nombre }: { id: string; nombre: string }) => {
      const { data, error } = await supabase
        .from('provincias')
        .update({ nombre })
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as Provincia;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.provincias }),
  });
}

export function useDeleteProvincia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('provincias').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.provincias });
      qc.invalidateQueries({ queryKey: ['catalogos', 'localidades'] });
    },
  });
}

// ── Localidades ──────────────────────────────────────────────────────────────

export function useLocalidades(provinciaId?: string) {
  return useQuery({
    queryKey: QK.localidades(provinciaId),
    queryFn: async () => {
      let q = supabase
        .from('localidades')
        .select('*, provincia:provincias(nombre)')
        .order('nombre');
      if (provinciaId) q = q.eq('provincia_id', provinciaId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as Localidad[];
    },
    staleTime: 60_000,
  });
}

export function useCreateLocalidad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { nombre: string; provincia_id: string }) => {
      const { data, error } = await supabase
        .from('localidades')
        .insert(payload)
        .select('*')
        .single();
      if (error) throw error;
      return data as Localidad;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['catalogos', 'localidades'] }),
  });
}

export function useUpdateLocalidad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: Partial<{ nombre: string; provincia_id: string }>;
    }) => {
      const { data, error } = await supabase
        .from('localidades')
        .update(payload)
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as Localidad;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['catalogos', 'localidades'] }),
  });
}

export function useDeleteLocalidad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('localidades').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['catalogos', 'localidades'] }),
  });
}

// ── Parentescos ──────────────────────────────────────────────────────────────

export function useParentescos() {
  return useQuery({
    queryKey: QK.parentescos,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('parentescos')
        .select('*')
        .order('descripcion');
      if (error) throw error;
      return (data ?? []) as Parentesco[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useCreateParentesco() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (descripcion: string) => {
      const { data, error } = await supabase
        .from('parentescos')
        .insert({ descripcion })
        .select('*')
        .single();
      if (error) throw error;
      return data as Parentesco;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.parentescos }),
  });
}

export function useUpdateParentesco() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, descripcion }: { id: string; descripcion: string }) => {
      const { data, error } = await supabase
        .from('parentescos')
        .update({ descripcion })
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as Parentesco;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.parentescos }),
  });
}

export function useDeleteParentesco() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('parentescos').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.parentescos }),
  });
}
